import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api, apiError } from "../lib/api";
import { Plus, Trash2, Upload, Save, ArrowLeft, ChevronDown, ChevronUp } from "lucide-react";
import PerformanceDashboard from "../components/PerformanceDashboard";
import { PaymentsAttention } from "../components/PaymentsAttention";

const uid = () => Math.random().toString(36).slice(2, 10);
const blank = () => ({ title: "New Course", subtitle: "", description: "", price: 0, cover_url: "", tag: "Course", published: false, order: 0, modules: [] });

async function uploadFile(file, access) {
  const fd = new FormData();
  fd.append("file", file);
  fd.append("access", access);
  const { data } = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
  return data; // {file_id, url, filename}
}

const Field = ({ label, children }) => (
  <div><label className="text-[11px] uppercase tracking-[0.2em] text-ink-soft">{label}</label>{children}</div>
);
const inputCls = "mt-2 w-full bg-transparent border border-line focus:border-ink outline-none px-4 py-3 text-ink font-light transition-colors";

export default function Admin() {
  const [tab, setTab] = useState("courses");
  const [courses, setCourses] = useState([]);
  const [editing, setEditing] = useState(null); // course object being edited
  const [enrollments, setEnrollments] = useState([]);
  const [leads, setLeads] = useState({ contacts: [], waitlist: [] });
  const [enrollForm, setEnrollForm] = useState({ email: "", course_id: "" });

  useEffect(() => { document.title = "Admin CMS · Il Salotto Digitale"; }, []);
  const loadCourses = () => api.get("/admin/courses").then((r) => setCourses(r.data));
  useEffect(() => { loadCourses(); }, []);
  useEffect(() => {
    if (tab === "enrollments") api.get("/admin/enrollments").then((r) => setEnrollments(r.data));
    if (tab === "leads") api.get("/admin/leads").then((r) => setLeads(r.data));
  }, [tab]);

  const saveCourse = async () => {
    try {
      const payload = { ...editing };
      if (editing.id) await api.put(`/admin/courses/${editing.id}`, payload);
      else await api.post("/admin/courses", payload);
      toast.success("Course saved.");
      setEditing(null);
      loadCourses();
    } catch (e) { toast.error(apiError(e)); }
  };
  const deleteCourse = async (id) => {
    if (!window.confirm("Delete this course permanently?")) return;
    await api.delete(`/admin/courses/${id}`);
    toast.success("Course deleted.");
    loadCourses();
  };

  const doEnroll = async (e) => {
    e.preventDefault();
    try {
      const { data } = await api.post("/admin/enroll", enrollForm);
      toast.success(data.already ? "Customer already had access." : "Customer enrolled.");
      setEnrollForm({ email: "", course_id: "" });
      api.get("/admin/enrollments").then((r) => setEnrollments(r.data));
    } catch (e2) { toast.error(apiError(e2)); }
  };

  // ---- Editor mutations ----
  const set = (patch) => setEditing((c) => ({ ...c, ...patch }));
  const setModules = (modules) => setEditing((c) => ({ ...c, modules }));
  const addModule = () => setModules([...(editing.modules || []), { id: uid(), title: "New Module", order: editing.modules.length, lessons: [] }]);
  const updateModule = (mi, patch) => setModules(editing.modules.map((m, i) => i === mi ? { ...m, ...patch } : m));
  const removeModule = (mi) => setModules(editing.modules.filter((_, i) => i !== mi));
  const addLesson = (mi) => updateModule(mi, { lessons: [...editing.modules[mi].lessons, { id: uid(), title: "New Lesson", order: editing.modules[mi].lessons.length, content: "", video_file_id: null, video_embed_url: "", resources: [] }] });
  const updateLesson = (mi, li, patch) => updateModule(mi, { lessons: editing.modules[mi].lessons.map((l, i) => i === li ? { ...l, ...patch } : l) });
  const removeLesson = (mi, li) => updateModule(mi, { lessons: editing.modules[mi].lessons.filter((_, i) => i !== li) });

  if (editing) return (
    <CourseEditor editing={editing} set={set} inputCls={inputCls} saveCourse={saveCourse} back={() => setEditing(null)}
      addModule={addModule} updateModule={updateModule} removeModule={removeModule}
      addLesson={addLesson} updateLesson={updateLesson} removeLesson={removeLesson} />
  );

  return (
    <div data-testid="admin-page" className="min-h-screen pt-20">
      <section className="mx-auto max-w-[1500px] px-6 lg:px-12 pt-14 pb-8 border-b border-line flex items-end justify-between">
        <div>
          <p className="text-[11px] uppercase tracking-ultra text-accent">Content Management</p>
          <h1 className="mt-3 font-serif font-light text-4xl lg:text-5xl text-ink tracking-tight">Admin CMS</h1>
        </div>
        <Link to="/account" className="text-[11px] uppercase tracking-[0.2em] text-ink-soft link-underline">← My account</Link>
      </section>

      <section className="mx-auto max-w-[1500px] px-6 lg:px-12 py-8">
        <div className="flex gap-6 border-b border-line mb-10">
          {["courses", "enrollments", "leads", "analytics"].map((t) => (
            <button key={t} data-testid={`admin-tab-${t}`} onClick={() => setTab(t)}
              className={`pb-4 text-[11px] uppercase tracking-[0.2em] transition-colors ${tab === t ? "text-ink border-b border-ink" : "text-ink-soft hover:text-ink"}`}>{t}</button>
          ))}
        </div>

        {tab === "courses" && (
          <div>
            <button data-testid="new-course-btn" onClick={() => setEditing(blank())}
              className="mb-8 bg-ink text-bg px-6 py-3 text-[11px] uppercase tracking-[0.2em] hover:bg-[#33312E] flex items-center gap-2"><Plus size={14} /> New course</button>
            <div className="border-t border-l border-line grid md:grid-cols-2 lg:grid-cols-3">
              {courses.map((c) => (
                <div key={c.id} data-testid={`admin-course-${c.id}`} className="border-b border-r border-line p-6">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <p className="text-[10px] uppercase tracking-[0.2em] text-accent">{c.tag} · {c.published ? "Published" : "Draft"}</p>
                      <h3 className="mt-1 font-serif text-2xl text-ink">{c.title}</h3>
                      <p className="mt-1 text-sm text-ink-soft">${Number(c.price).toFixed(2)} · {c.modules?.length || 0} modules</p>
                    </div>
                  </div>
                  <div className="mt-5 flex gap-3">
                    <Link to={`/learn/${c.id}`} data-testid={`preview-course-${c.id}`} className="border border-ink px-4 py-2 text-[11px] uppercase tracking-[0.2em] hover:bg-ink hover:text-bg transition-colors">Preview</Link>
                    <button data-testid={`edit-course-${c.id}`} onClick={() => setEditing(JSON.parse(JSON.stringify(c)))} className="border border-ink px-4 py-2 text-[11px] uppercase tracking-[0.2em] hover:bg-ink hover:text-bg transition-colors">Edit</button>
                    <button onClick={() => deleteCourse(c.id)} className="border border-line px-4 py-2 text-[11px] uppercase tracking-[0.2em] text-ink-soft hover:border-ink hover:text-ink flex items-center gap-1"><Trash2 size={12} /> Delete</button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {tab === "enrollments" && (
          <div className="grid lg:grid-cols-3 gap-12">
            <form onSubmit={doEnroll} className="lg:col-span-1 border border-line p-6 h-fit" data-testid="manual-enroll-form">
              <p className="text-[11px] uppercase tracking-ultra text-accent mb-4">Manual enrolment</p>
              <p className="text-xs text-ink-soft mb-5">Grant a customer access manually. They must have an account (any purchase or sign-up creates one).</p>
              <Field label="Customer email"><input required type="email" className={inputCls} value={enrollForm.email} onChange={(e) => setEnrollForm({ ...enrollForm, email: e.target.value })} data-testid="enroll-email" /></Field>
              <div className="mt-4"><Field label="Course">
                <select required className={inputCls} value={enrollForm.course_id} onChange={(e) => setEnrollForm({ ...enrollForm, course_id: e.target.value })} data-testid="enroll-course">
                  <option value="">Select a course…</option>
                  {courses.map((c) => <option key={c.id} value={c.id}>{c.title}</option>)}
                </select>
              </Field></div>
              <button className="mt-6 w-full bg-ink text-bg py-3 text-[11px] uppercase tracking-[0.2em] hover:bg-[#33312E]" data-testid="enroll-submit">Grant access</button>
            </form>
            <div className="lg:col-span-2">
              <PaymentsAttention />
              <p className="text-[11px] uppercase tracking-ultra text-ink-soft mb-4">Enrollments ({enrollments.length})</p>
              <div className="border border-line divide-y divide-line">
                {enrollments.length === 0 && <p className="p-6 text-sm text-ink-soft">No enrollments yet.</p>}
                {enrollments.map((e) => (
                  <div key={e.id} className="p-4 flex justify-between text-sm">
                    <span className="text-ink">{e.user_name} <span className="text-ink-soft">· {e.user_email}</span></span>
                    <span className="text-ink-soft">{e.course_title} · <span className="uppercase text-[10px] tracking-[0.15em]">{e.source}</span></span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {tab === "leads" && (
          <div className="grid lg:grid-cols-2 gap-12">
            <div>
              <p className="text-[11px] uppercase tracking-ultra text-ink-soft mb-4">Contact messages ({leads.contacts.length})</p>
              <div className="border border-line divide-y divide-line">
                {leads.contacts.length === 0 && <p className="p-6 text-sm text-ink-soft">No messages yet.</p>}
                {leads.contacts.map((c) => (
                  <div key={c.id} className="p-4 text-sm">
                    <p className="text-ink font-medium">{c.name} <span className="text-ink-soft font-light">· {c.email}</span></p>
                    <p className="text-[10px] uppercase tracking-[0.15em] text-accent mt-1">{c.interest}</p>
                    <p className="text-ink-soft mt-2 font-light">{c.message}</p>
                  </div>
                ))}
              </div>
            </div>
            <div>
              <p className="text-[11px] uppercase tracking-ultra text-ink-soft mb-4">Waitlist / enquiries ({leads.waitlist.length})</p>
              <div className="border border-line divide-y divide-line">
                {leads.waitlist.length === 0 && <p className="p-6 text-sm text-ink-soft">No enquiries yet.</p>}
                {leads.waitlist.map((w) => (
                  <div key={w.id} className="p-4 text-sm">
                    <p className="text-ink font-medium">{w.name} <span className="text-ink-soft font-light">· {w.email}</span></p>
                    <p className="text-[10px] uppercase tracking-[0.15em] text-accent mt-1">{w.product} · {w.product_type}</p>
                    {w.note && <p className="text-ink-soft mt-2 font-light">{w.note}</p>}
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {tab === "analytics" && (
          <div data-testid="analytics-tab">
            <h2 className="font-serif font-light text-3xl lg:text-4xl text-ink tracking-tight mb-8">Analytics</h2>
            <PerformanceDashboard />
          </div>
        )}

      </section>
    </div>
  );
}
function UploadBtn({ label, access, onDone, accept }) {
  const [busy, setBusy] = useState(false);
  return (
    <label className={`cursor-pointer border border-line px-4 py-2 text-[11px] uppercase tracking-[0.2em] hover:border-ink transition-colors inline-flex items-center gap-2 ${busy ? "opacity-60" : ""}`}>
      <Upload size={12} /> {busy ? "Uploading…" : label}
      <input type="file" hidden accept={accept} onChange={async (e) => {
        const f = e.target.files[0]; if (!f) return;
        setBusy(true);
        try { const d = await uploadFile(f, access); onDone(d); toast.success("Uploaded."); }
        catch (err) { toast.error(apiError(err)); }
        finally { setBusy(false); e.target.value = ""; }
      }} />
    </label>
  );
}

function CourseEditor({ editing, set, inputCls, saveCourse, back, addModule, updateModule, removeModule, addLesson, updateLesson, removeLesson }) {
  const [openModule, setOpenModule] = useState(0);
  return (
    <div data-testid="course-editor" className="min-h-screen pt-20">
      <div className="sticky top-20 z-30 bg-bg/95 backdrop-blur border-b border-line">
        <div className="mx-auto max-w-[1300px] px-6 lg:px-12 py-4 flex items-center justify-between">
          <button onClick={back} className="text-[11px] uppercase tracking-[0.2em] text-ink-soft link-underline flex items-center gap-1"><ArrowLeft size={14} /> Back</button>
          <button data-testid="save-course" onClick={saveCourse} className="bg-ink text-bg px-8 py-3 text-[11px] uppercase tracking-[0.2em] hover:bg-[#33312E] flex items-center gap-2"><Save size={14} /> Save course</button>
        </div>
      </div>

      <div className="mx-auto max-w-[1300px] px-6 lg:px-12 py-10 space-y-8">
        {/* Basics */}
        <div className="grid lg:grid-cols-2 gap-6">
          <div><label className="text-[11px] uppercase tracking-[0.2em] text-ink-soft">Title</label>
            <input data-testid="course-title" className={inputCls} value={editing.title} onChange={(e) => set({ title: e.target.value })} /></div>
          <div><label className="text-[11px] uppercase tracking-[0.2em] text-ink-soft">Subtitle</label>
            <input className={inputCls} value={editing.subtitle} onChange={(e) => set({ subtitle: e.target.value })} /></div>
        </div>
        <div><label className="text-[11px] uppercase tracking-[0.2em] text-ink-soft">Description</label>
          <textarea rows={3} className={inputCls} value={editing.description} onChange={(e) => set({ description: e.target.value })} /></div>
        <div className="grid lg:grid-cols-4 gap-6 items-end">
          <div><label className="text-[11px] uppercase tracking-[0.2em] text-ink-soft">Price (USD)</label>
            <input data-testid="course-price" type="number" step="0.01" className={inputCls} value={editing.price} onChange={(e) => set({ price: parseFloat(e.target.value) || 0 })} /></div>
          <div><label className="text-[11px] uppercase tracking-[0.2em] text-ink-soft">Tag / Label</label>
            <input className={inputCls} value={editing.tag} onChange={(e) => set({ tag: e.target.value })} /></div>
          <div><label className="text-[11px] uppercase tracking-[0.2em] text-ink-soft">Order</label>
            <input type="number" className={inputCls} value={editing.order} onChange={(e) => set({ order: parseInt(e.target.value) || 0 })} /></div>
          <label className="flex items-center gap-3 pb-3 cursor-pointer">
            <input type="checkbox" data-testid="course-published" checked={editing.published} onChange={(e) => set({ published: e.target.checked })} className="w-4 h-4 accent-[#1C1B1A]" />
            <span className="text-[11px] uppercase tracking-[0.2em] text-ink">Published</span>
          </label>
        </div>

        {/* Cover */}
        <div className="border border-line p-6">
          <label className="text-[11px] uppercase tracking-[0.2em] text-ink-soft">Cover image</label>
          <div className="mt-3 flex items-center gap-6">
            {editing.cover_url ? <img src={editing.cover_url} alt="cover" className="w-40 aspect-[16/10] object-cover" /> : <div className="w-40 aspect-[16/10] bg-surface flex items-center justify-center text-xs text-ink-soft">No cover</div>}
            <div className="space-y-3">
              <UploadBtn label="Upload cover" access="public" accept="image/*" onDone={(d) => set({ cover_url: `${process.env.REACT_APP_BACKEND_URL}${d.url}` })} />
              <input className={inputCls + " min-w-[280px]"} placeholder="…or paste an image URL" value={editing.cover_url || ""} onChange={(e) => set({ cover_url: e.target.value })} />
            </div>
          </div>
        </div>

        {/* Modules */}
        <div>
          <div className="flex items-center justify-between mb-4">
            <p className="text-[11px] uppercase tracking-ultra text-accent">Modules & Lessons</p>
            <button data-testid="add-module" onClick={addModule} className="border border-ink px-4 py-2 text-[11px] uppercase tracking-[0.2em] hover:bg-ink hover:text-bg flex items-center gap-2"><Plus size={12} /> Add module</button>
          </div>

          {editing.modules.map((m, mi) => (
            <div key={m.id} className="border border-line mb-4" data-testid={`module-${mi}`}>
              <div className="flex items-center gap-3 p-4 bg-surface">
                <button onClick={() => setOpenModule(openModule === mi ? -1 : mi)} className="text-ink-soft">{openModule === mi ? <ChevronUp size={16} /> : <ChevronDown size={16} />}</button>
                <input className="flex-1 bg-transparent border-b border-line focus:border-ink outline-none py-1 text-ink font-serif text-xl" value={m.title} onChange={(e) => updateModule(mi, { title: e.target.value })} />
                <input type="number" className="w-16 bg-transparent border-b border-line py-1 text-ink-soft text-sm" value={m.order} onChange={(e) => updateModule(mi, { order: parseInt(e.target.value) || 0 })} title="order" />
                <button onClick={() => removeModule(mi)} className="text-ink-soft hover:text-ink"><Trash2 size={16} /></button>
              </div>

              {openModule === mi && (
                <div className="p-4 space-y-4">
                  {m.lessons.map((l, li) => (
                    <div key={l.id} className="border border-line p-4" data-testid={`lesson-${mi}-${li}`}>
                      <div className="flex items-center gap-3 mb-4">
                        <input className="flex-1 bg-transparent border-b border-line focus:border-ink outline-none py-1 text-ink text-lg" value={l.title} onChange={(e) => updateLesson(mi, li, { title: e.target.value })} />
                        <input type="number" className="w-16 bg-transparent border-b border-line py-1 text-ink-soft text-sm" value={l.order} onChange={(e) => updateLesson(mi, li, { order: parseInt(e.target.value) || 0 })} title="order" />
                        <button onClick={() => removeLesson(mi, li)} className="text-ink-soft hover:text-ink"><Trash2 size={15} /></button>
                      </div>
                      <label className="text-[11px] uppercase tracking-[0.2em] text-ink-soft">Lesson text (HTML allowed)</label>
                      <textarea rows={4} className={inputCls} value={l.content} onChange={(e) => updateLesson(mi, li, { content: e.target.value })} placeholder="<p>Write your lesson…</p>" />

                      <div className="mt-4 flex flex-wrap items-center gap-4">
                        <UploadBtn label={l.video_file_id ? "Replace video" : "Upload video"} access="protected" accept="video/*" onDone={(d) => updateLesson(mi, li, { video_file_id: d.file_id })} />
                        {l.video_file_id && <span className="text-xs text-ink-soft">Video attached ✓</span>}
                        <input className={inputCls + " flex-1 min-w-[220px] mt-0"} placeholder="or embed URL (Vimeo/YouTube)" value={l.video_embed_url || ""} onChange={(e) => updateLesson(mi, li, { video_embed_url: e.target.value })} />
                      </div>

                      <div className="mt-4">
                        <div className="flex items-center justify-between mb-2">
                          <label className="text-[11px] uppercase tracking-[0.2em] text-ink-soft">Resources & downloads</label>
                          <UploadBtn label="Add file" access="protected" accept=".pdf,.docx,.csv,.txt,image/*"
                            onDone={(d) => updateLesson(mi, li, { resources: [...(l.resources || []), { id: uid(), name: d.filename, file_id: d.file_id, kind: "download" }] })} />
                        </div>
                        {(l.resources || []).map((r, ri) => (
                          <div key={r.id} className="flex items-center gap-3 py-2 border-b border-line">
                            <input className="flex-1 bg-transparent border-b border-transparent focus:border-line outline-none py-1 text-sm text-ink" value={r.name} onChange={(e) => updateLesson(mi, li, { resources: l.resources.map((x, i) => i === ri ? { ...x, name: e.target.value } : x) })} />
                            <span className="text-[10px] uppercase tracking-[0.15em] text-ink-soft">{r.kind}</span>
                            <button onClick={() => updateLesson(mi, li, { resources: l.resources.filter((_, i) => i !== ri) })} className="text-ink-soft hover:text-ink"><Trash2 size={13} /></button>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                  <button data-testid={`add-lesson-${mi}`} onClick={() => addLesson(mi)} className="border border-line px-4 py-2 text-[11px] uppercase tracking-[0.2em] text-ink-soft hover:border-ink hover:text-ink flex items-center gap-2"><Plus size={12} /> Add lesson</button>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
